
/**
 * Overlay API
 *
 * To open an overlay, one of the following methods can be used:
 * 1) add data-overlay="overlay-id" to the trigger element
 * 2) use the href attribute of an anchor that points to the id of the overlay. In this case, the overlay id must always start with "overlay-".
 * 3) use an url in the href attribute ending with the #overlayId of the external element that needs to load in the overlay along with the data-overlay attribute
 * 4) to open an overlay on load, just add the class overlay--opened to the overlay element. The script will take care of the rest.
 *
 * To use templates in the overlay:
 * - a data-overlay-data attribute must be added to the trigger element, containing a valid json oject.
 * - the overlay content (.ovrlay-content) must have a single child, which should be the template element that will be rendered using the data (it uses Mustache as a templating engine).
 *
 * To close an overlay use one of the following:
 * 1) add data-overlay-close to the element you want to trigger the closing
 * 2) add .overlay-close to the element you want to trigger the closing
 */
class Overlay {
    /**
     * Constructor
     * @param {Element} overlayElement
     */
    constructor(overlayElement) {
        this.overlayElement = overlayElement;
        this.templateElement = this.overlayElement.firstElementChild.querySelector('template');

        if (this.templateElement) {
            this.template = this.templateElement.innerHTML;
        }

        this.overlayElement.Overlay = this;
        this.registerEvents();
    }

    /**
     * Register events
     */
    registerEvents() {
        document.body.addEventListener('click', delegate('[data-overlay="' + this.overlayElement.id + '"], [href^="#' + this.overlayElement.id + '"]', (e) => this.prepareToOpen(e.delegateTarget)));
        document.body.addEventListener('click', delegate('#' + this.overlayElement.id + ' [data-overlay-close], #' + this.overlayElement.id + ' .overlay-close', (e) => this.close()));
    }


    /**
     * Prepare to open
     * @param {Element} triggerElement
     */
    prepareToOpen(triggerElement) {
        this.open(triggerElement, getValidJSON(triggerElement.getAttribute('data-overlay-data')));
    }

    /**
     * Open
     * @param {Element} triggerElement
     * @param {Object} [data={}]
     */
    open(triggerElement = null, data = {}) {
        this.closeAllOthers();

        let openEvent = new CustomEvent('overlay:open', {detail: {
            'triggerElement': triggerElement,
            'overlayElement': this.overlayElement
        }});

        let rendered = this.render(data);

        document.documentElement.classList.add('overlay-isopened');
        document.body.dispatchEvent(openEvent);
        this.overlayElement.dispatchEvent(openEvent);

        this.overlayElement.classList.add('overlay--opened');
    }


    /**
     * Close
     */
    close() {
        if (!this.isOpened()) return;

        let closeEvent = new CustomEvent('overlay:close', {detail: {
            'overlayElement': this.overlayElement,
        }});

        this.overlayElement.classList.remove('overlay--opened');
        document.documentElement.classList.remove('overlay-isopened');

        if (window.location.hash == '#' + this.overlayElement.id) {
            window.location.hash = '';
        }

        document.body.dispatchEvent(closeEvent);
        this.overlayElement.dispatchEvent(closeEvent);
    }


    /**
     * Close all others
     */
    closeAllOthers() {
        let openedOverlays = Array.from(document.querySelectorAll('.overlay--opened, .overlay' + window.location.hash));
        openedOverlays.forEach((overlayElement) => {
            if (!overlayElement.Overlay || overlayElement.id == this.overlayElement.id) return;
            overlayElement.Overlay.close();
        });
    }


    /**
     * Render template
     * @param Object data
     */
    render(data = {}) {
        if (!this.template || !data || typeof data !== 'object' || (typeof data === 'object' && !Object.keys(data).length)) return false;

        let renderEvent = new CustomEvent('overlay:render', {detail: {
            'overlayElement': this.overlayElement
        }});

        let contentElement = this.overlayElement.firstElementChild;

        while (contentElement.firstChild) {
            contentElement.removeChild(contentElement.firstChild);
        }

        contentElement.innerHTML = Mustache.render(this.template, data);

        document.body.dispatchEvent(renderEvent);
        this.overlayElement.dispatchEvent(renderEvent);
        return true;
    }


    /**
     * Is opened
     * @return {Boolean} if the overlay is opened or not
     */
    isOpened() {
        return (this.overlayElement.matches('.overlay--opened'));
    }
}


// Overlay trigger
class OverlayTrigger {
    /**
     * Constructor
     * @param {Element} triggerElement
     */
    constructor(triggerElement) {
        this.triggerElement = triggerElement;
        this.overlayIdentifier = this.triggerElement.getAttribute('data-overlay') || (this.triggerElement.hasAttribute('href') ? this.triggerElement.getAttribute('href').split('#')[1] : '');
        this.overlayElement = document.getElementById(this.overlayIdentifier.startsWith('overlay-') ? this.overlayIdentifier : 'overlay-' + this.overlayIdentifier);

        if (this.overlayElement) {
            this.Overlay = !this.overlayElement.Overlay ? new Overlay(this.overlayElement) : this.overlayElement.Overlay;
        }

        this.triggerElement.OverlayTrigger = this;

        this.structureTemplateElement = document.querySelector('template#overlay');
        if (this.structureTemplateElement) {
            this.structureTemplate = this.structureTemplateElement.innerHTML;
        }

        this.registerEvents();
    }


    /**
     * Register events
     */
    registerEvents() {
        this.triggerElement.addEventListener('click', (e) => this.prepareToActivate(e));
    }

    /**
     * Prepare to activate
     * @param {Event} e
     */
    prepareToActivate(e) {
        e.preventDefault();
        if (this.overlayElement) {
            this.Overlay.prepareToOpen(this.triggerElement);
        } else if (this.triggerElement.href) {
            this.loadContent();
        }
    }

    /**
     * Load content
     */
    loadContent() {
        document.body.classList.add('loading');

        fetch(this.triggerElement.getAttribute('href'), this.requestInitTemplate)
            .then((response) => {
                return response.text();
            })
            .then((loadedContent) => this.processLoadedContent(loadedContent))
            .catch((e) => {
                this.finishedLoading();
                console.log(e);
                return false;
            });
    }

    /**
     * Finished loading
     */
    finishedLoading() {
        document.body.classList.remove('loading');
    }

    /**
     * Request init template
     * @return {Object}
     */
    get requestInitTemplate() {
        return {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin'
        };
    }


    /**
     * Parse loaded content
     * @param {String} loadedContent
     */
    processLoadedContent(loadedContent) {
        this.finishedLoading();

        let parser = new DOMParser();
        let loadedDocument = parser.parseFromString(loadedContent, 'text/html');

        let overlayContentElement = loadedDocument.getElementById(this.overlayIdentifier);

        if (!overlayContentElement) {
            console.error('The targeted overlay element was not found');
            return;
        }

        if (this.createStructure(overlayContentElement.outerHTML)) {
            this.Overlay.prepareToOpen(this.triggerElement);
        }

    }


    /**
     * Create overlay structure
     * @param {String} overlayContent
     * @return {Bool}
     */
    createStructure(overlayContent) {
        if (!this.structureTemplate) {
            console.error('The overlay component needs to have a template to be able to fetch overlays and display them in the current page');
            return false;
        }

        this.structureTemplateElement.parentNode.insertAdjacentHTML('beforeend', Mustache.render(this.structureTemplate, {
            content: overlayContent,
            id: this.overlayIdentifier,
        }));

        this.overlayElement = document.getElementById('overlay-' + this.overlayIdentifier);

        if (!this.overlayElement) {
            return false;
        }

        this.Overlay = new Overlay(this.overlayElement);
        return true;
    }

}


class OverlayHandler {

    /**
     * Constructor
     */
    constructor() {
        this.triggerSelector = '[data-overlay], [href*="#overlay-"]';
        this.registerEvents();
    }


    /**
     * Register events
     */
    registerEvents() {
        // Make sure the first opened overlay (we just ignore the others, since only an overlay can be opened at a time) is setuped on loaded
        document.addEventListener('DOMContentLoaded', (e) => {
            let overlayElement = document.querySelector('.overlay--opened');
            if (!overlayElement) return;

            let overlayObject = !overlayElement.Overlay ? new Overlay(overlayElement) : overlayElement.Overlay;
            overlayObject.open(overlayElement);
        });

        document.body.addEventListener('click', delegate(this.triggerSelector, (e) => {
            this.setupTrigger(e.delegateTarget);
            e.delegateTarget.OverlayTrigger.prepareToActivate(e);
        }));
    }


    /**
     * Setup trigger Element
     * @param {Element} triggerElement
     */
    setupTrigger(triggerElement) {
        if (triggerElement.OverlayTrigger) return;
        new OverlayTrigger(triggerElement);
    }

};

new OverlayHandler();
